// src/js/home/cindy-modal.ts
//
// The sixth home-dashboard modal (CINDY-06/07/09, P-14/P-15/P-18) — built from day one to
// Phase 262's date-nav contract (setActiveStepper registered before the modal shows, a bare
// date-free title, the shared _busy lockout inherited rather than duplicated). Mounts the
// SAME shared entry-form component `/workouts` uses (`mountCindyEntry()`, 263-05) rather than
// rebuilding any paste box / round repeater of its own, and saves through the SAME thin-wrapper
// signal topology WorkoutsPresenter uses (`?do=saveCindySession` -> HomePresenter ->
// CindyModel::saveSession(), never a second write path).
//
// Deliberately imports ONLY from ../cindy/entry-form (never the note parser or the row
// repeater modules directly — those are entry-form.ts's own concern) and from
// ./shared/./modal (this module's own siblings).

import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import { findChart, setActiveStepper } from './shared';
import { clearModalOwnership } from './modal';
import { mountCindyEntry, CindyEntrySession, CindyEntryController } from '../cindy/entry-form';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Module state
let currentDate = '';
let currentController: CindyEntryController | null = null;
let currentSessionId: number | null = null;
let currentRoundCount = 0;
let hiddenListenerAttached = false;
let modalListenersBound = false;

export function initCindyModal(): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    if (!hiddenListenerAttached) {
        modalEl.addEventListener('hidden.bs.modal', () => {
            // Defensively restore footer (idempotent — also handled by the other init*Modal fns;
            // every sibling's hidden.bs.modal listener runs on ANY modal close, not just its own).
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (footer) footer.classList.remove('d-none');
            currentController?.destroy();
            currentController = null;
            currentSessionId = null;
            currentRoundCount = 0;
            currentDate = '';
        });
        hiddenListenerAttached = true;
    }

    if (!modalListenersBound) {
        modalListenersBound = true;

        // Self-select guard: `.js-modal-save`/`.js-modal-delete` are the SAME shared buttons
        // modal.ts's own initModal() already bound listeners to (see clearModalOwnership()'s doc
        // comment in modal.ts) — `currentController` is only non-null while THIS modal's content
        // is the one actually mounted, so a click while a different modal type is open is a no-op.
        const saveBtn = modalEl.querySelector('.js-modal-save') as HTMLButtonElement | null;
        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                if (!currentController) return;
                handleCindySave(saveBtn);
            });
        }

        const deleteBtn = modalEl.querySelector('.js-modal-delete') as HTMLButtonElement | null;
        if (deleteBtn) {
            deleteBtn.addEventListener('click', () => {
                if (currentSessionId === null) return;
                handleCindyDelete(deleteBtn);
            });
        }
    }
}

/**
 * Fetch-and-mount half of openCindyModal() — extracted so the date-nav step path (cindyStep)
 * can reload the body for a new date without closing/reopening the modal (262 P-04).
 *
 * Open path (blankUntilLoaded=false): mounts a blank create-mode form synchronously (D-01
 * show-first — the modal has real, focusable content the instant it is shown), then
 * getCindyFromDate's response refreshes it IN PLACE via the controller's own refresh() if a
 * session already exists for this date — never rebuilds the root (entry-form.ts's own contract).
 *
 * Step path (blankUntilLoaded=true): the body is left EMPTY (P-04 — no skeleton) until the fetch
 * resolves, then the whole form is mounted fresh from the response.
 *
 * The shared footer is hidden for the duration of every fetch (both paths) and revealed only
 * once real data has resolved — Save/Delete are never visible while `currentController`/
 * `currentSessionId` could still be stale from a previous date.
 */
function renderBodyForDate(date: string, blankUntilLoaded: boolean, done?: (ok: boolean) => void): void {
    const modalEl = document.getElementById('chart-edit-modal');
    const body = document.getElementById('chart-edit-modal-body');
    if (!modalEl || !body) { done?.(false); return; }

    const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
    footer?.classList.add('d-none');

    body.replaceChildren();
    currentController = null;

    let openController: CindyEntryController | null = null;
    if (!blankUntilLoaded) {
        const mountEl = document.createElement('div');
        body.appendChild(mountEl);
        openController = mountCindyEntry(mountEl, null, { context: 'modal' });
        currentController = openController;
    }

    get('?do=getCindyFromDate&date=' + date, {}, (data: any) => {
        // CR-01 stale-response guard (mirrors gym-modal.ts/mood-modal.ts) — the open path's own
        // fetch is not covered by date-nav's _busy lock, so opening the modal and immediately
        // stepping can leave two getCindyFromDate requests in flight for two different dates.
        if (date !== currentDate) { done?.(true); return; }

        if (!data || data.status !== 1) { done?.(false); return; }

        const session: CindyEntrySession | null = data.session ?? null;
        currentSessionId = session ? session.id : null;
        currentRoundCount = session ? session.rounds.length : 0;

        if (blankUntilLoaded) {
            const mountEl = document.createElement('div');
            body.appendChild(mountEl);
            currentController = mountCindyEntry(mountEl, session, { context: 'modal' });
        } else {
            openController!.refresh(session);
        }

        footer?.classList.remove('d-none');
        const deleteBtn = modalEl.querySelector('.js-modal-delete') as HTMLButtonElement | null;
        deleteBtn?.classList.toggle('d-none', session === null);

        done?.(true);
    });
}

/**
 * Date-nav step callback (262 P-04) — the ModalDateStepper handed to shared.ts's active-stepper
 * registry from inside openCindyModal(). Inherits date-nav.ts's own `_busy` lockout entirely —
 * no second in-flight guard is added here.
 */
function cindyStep(newDate: string, done: (ok: boolean) => void): void {
    currentDate = newDate;
    renderBodyForDate(newDate, true, done);
}

export function openCindyModal(date: string): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    currentDate = date;
    currentSessionId = null;
    currentRoundCount = 0;

    // T-263 fix (see modal.ts's clearModalOwnership() doc comment): release modal.ts's ownership
    // of the shared footer BEFORE this modal reveals it, so a stale currentChartType left over
    // from an earlier alc/weight/snap/etc. edit this session can never also fire on a Cindy
    // save/delete click.
    clearModalOwnership();

    // 262 P-04 hard requirement — registers with date-nav.ts BEFORE the modal is shown.
    setActiveStepper(date, cindyStep);

    // 262 P-01: bare, date-free title — set once, never touched again by cindyStep.
    const title = document.getElementById('chart-edit-modal-title');
    if (title) title.textContent = 'Cindy';

    const saveLabel = modalEl.querySelector('.js-modal-save-label') as HTMLElement | null;
    if (saveLabel) saveLabel.textContent = 'Save session';

    renderBodyForDate(date, false);

    const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true });
    modalInstance.show();
}

function clearInlineError(): void {
    document.querySelector('#chart-edit-modal-body .modal-inline-error')?.remove();
}

/** Same `.modal-inline-error` class/treatment modal.ts's own saveModal()/deleteModal() use. */
function showInlineError(message: string): void {
    clearInlineError();
    const body = document.getElementById('chart-edit-modal-body');
    if (!body) return;
    const errorEl = document.createElement('div');
    errorEl.className = 'text-danger small mt-2 modal-inline-error';
    errorEl.textContent = message;
    body.appendChild(errorEl);
}

/**
 * Array-of-pairs, not a plain object — mirrors workouts.ts's buildSavePayload() exactly (both
 * surfaces need the identical URLSearchParams shape so CindyModel::saveSession()'s
 * `$post['rounds'] ?? []` reads back a PHP array, not one comma-joined string). Small plumbing
 * duplicated per-surface deliberately (P-18/T-263-12: cindy-modal.ts and workouts.ts each import
 * only from src/js/cindy/, never from each other) — not a copy of the shared entry/parser/
 * repeater component itself.
 */
function buildSavePayload(payload: ReturnType<CindyEntryController['collectPayload']>): [string, string][] {
    const pairs: [string, string][] = [
        ['session_id', payload.session_id !== null ? String(payload.session_id) : ''],
        ['date', payload.date],
        ['rounds_completed', String(payload.rounds_completed)],
        ['partial_reps', String(payload.partial_reps)],
        ['note', payload.note],
    ];
    payload.rounds.forEach((duration) => {
        pairs.push(['rounds[]', duration]);
    });
    return pairs;
}

/** Pure string/number arithmetic only (P-11) — never a Date object, never a timezone conversion. */
function formatMonthDayYear(dateStr: string): string {
    const parts = dateStr.split('-');
    const year = parts[0];
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    return MONTH_NAMES[month - 1] + ' ' + day + ', ' + year;
}

function handleCindySave(saveBtn: HTMLButtonElement): void {
    if (saveBtn.disabled) return;   // re-entrancy guard — mirrors gym-modal's handleGymSaveAll (D-04)
    if (!currentController) return;

    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    clearInlineError();

    const payload = currentController.collectPayload();

    const saveLabel = modalEl.querySelector('.js-modal-save-label') as HTMLElement | null;
    const spinner = modalEl.querySelector('.js-modal-spinner') as HTMLElement | null;
    saveBtn.disabled = true;
    saveLabel?.classList.add('d-none');
    spinner?.classList.remove('d-none');

    // post() (src/js/util.ts) sets the 'X-Requested-With: XMLHttpRequest' header unconditionally
    // on every call — the exact header BasePresenter::processSignal()'s AJAX gate requires
    // (T-263-02b), matching every sibling modal's own AJAX helper usage.
    post('?do=saveCindySession', buildSavePayload(payload), (data: any) => {
        saveBtn.disabled = false;
        saveLabel?.classList.remove('d-none');
        spinner?.classList.add('d-none');

        if (data && data.status === 1) {
            // Cindy's decomposed per-movement reps (pull-ups/push-ups/squats) can only be
            // derived by CindyModel::movementTotals() — re-implementing that arithmetic here
            // would be the second copy of it the model's own docblock forbids. Re-fetching the
            // authoritative series (mirrors modal.ts's refreshSnapChart() precedent) also
            // correctly handles P-13's best-of-day rule on delete/edit, which a client-side
            // point.update() could not.
            refreshCindyChart();
            const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
            modalInstance.hide();
            notifier.success('Saved');
        } else {
            showInlineError((data && data.message) || 'Save failed.');
        }
    });
}

function handleCindyDelete(deleteBtn: HTMLButtonElement): void {
    if (deleteBtn.disabled) return;
    if (currentSessionId === null) return;

    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    // UI-SPEC's exact Copywriting Contract copy — 'Delete this session?' title, 'This removes
    // {Mon D, Year}'s session and all {N} round times. This cannot be undone.' body (same text
    // workouts.ts's own bindDelete() uses).
    const dateDisplay = formatMonthDayYear(currentDate);
    const confirmed = window.confirm(
        'Delete this session?\n\nThis removes ' + dateDisplay + "'s session and all " +
        currentRoundCount + ' round times. This cannot be undone.'
    );
    if (!confirmed) return;

    clearInlineError();
    deleteBtn.disabled = true;

    post('?do=deleteCindySession', { session_id: currentSessionId }, (data: any) => {
        deleteBtn.disabled = false;

        if (data && data.status === 1) {
            refreshCindyChart();
            const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
            modalInstance.hide();
            notifier.success('Deleted');
        } else {
            showInlineError((data && data.message) || 'Delete failed.');
        }
    });
}

/**
 * Re-fetches the authoritative three-series Cindy chart data and swaps it in place — the same
 * "single source of truth, re-fetch rather than derive client-side" pattern modal.ts's
 * refreshSnapChart() already establishes for exactly this class of problem (a value this module
 * cannot correctly recompute itself). No titlebar totals exist for the `cindy` graph
 * (HomeModel::data_array['cindy'] carries no 'totals' key), so unlike gym/weight there is nothing
 * to pass to updateChartTotals() here.
 */
function refreshCindyChart(): void {
    const chart = findChart('chart_cindy');
    if (!chart) return;
    post('?do=fetchGraphData&key=cindy', {}, (resp: any) => {
        if (!resp || resp.success !== true || !resp.graph || !resp.graph.data) return;
        const seriesData = resp.graph.data;
        for (let i = 0; i < 3; i++) {
            const series = chart.series[i];
            const data = seriesData[i];
            if (series && data) {
                //@ts-ignore
                series.setData(data, false);
            }
        }
        chart.redraw();
    });
}
